const MAX_HEIGHT = 300;

const handleKeyDown = (e: KeyboardEvent) => {
  if (e.key === "Escape" || e.key === " ") {
    e.stopPropagation();
    e.preventDefault();
    const panel = e.currentTarget as HTMLElement;
    closePanel(panel);
  }
};

const handleFocusOut = (e: FocusEvent) => {
  const panel = e.currentTarget as HTMLElement;
  const focusedElement = e.relatedTarget as HTMLElement | null;
  if (!focusedElement || (panel && !panel.contains(focusedElement))) {
    closePanel(panel);
  }
};

const handleButtonClick = (e: Event) => {
  const toggleButton = e.currentTarget as HTMLElement;
  const dropdownPanel = findPanel(toggleButton);
  if (!dropdownPanel) {
    return;
  }

  const isOpen = toggleButton.getAttribute("aria-expanded") === "true";
  if (isOpen) {
    closeDropdown(toggleButton, dropdownPanel);
  } else {
    openDropdown(toggleButton, dropdownPanel);
  }
};

const findPanel = (toggleButton: Element) => {
  const panelId = toggleButton.getAttribute("aria-controls");
  if (!panelId) {
    console.warn("Dropdown toggle button is missing a panel ID");
    return null;
  }

  const dropdownPanel = document.getElementById(panelId);
  if (!dropdownPanel) {
    console.warn(`Dropdown panel with ID ${panelId} does not exist`);
    return null;
  }

  return dropdownPanel as HTMLElement | null;
};

const findToggleButton = (panel: Element) => {
  const button = document.querySelector(`[aria-controls="${panel.id}"]`);
  if (!button) {
    console.warn(`Dropdown panel "${panel.id}" is missing a toggle button`);
  }

  return button as HTMLElement | null;
};

const closePanel = (panel: HTMLElement) => {
  const button = findToggleButton(panel);
  if (button) {
    closeDropdown(button, panel);
  }
};

const closeDropdown = (button: HTMLElement, panel: HTMLElement) => {
  button.setAttribute("aria-expanded", "false");
  button.setAttribute("data-expanded", "false");

  panel.style.height = panel.children[0].clientHeight + "px";

  // After browser has updated the fixed height (a few milliseconds to be sure), set to 0
  setTimeout(() => {
    panel.style.height = "0";

    // Clear specific height after duration
    setTimeout(() => panel.style.removeProperty("height"), 200);
  }, 10);

  toggleAllClasses(button, false);
};

const openDropdown = (button: HTMLElement, panel: HTMLElement) => {
  button.setAttribute("aria-expanded", "true");
  button.setAttribute("data-expanded", "true");

  panel.dataset.dropdownDirection = isDirectionUp(button) ? "up" : "down";

  toggleAllClasses(button, true);

  panel.style.height = "0";

  // After browser has updated the fixed height (a few milliseconds to be sure), set to 0
  setTimeout(() => {
    panel.style.height = panel.children[0].clientHeight + "px";

    // Clear specific height after duration
    setTimeout(() => panel.style.removeProperty("height"), 200);
  }, 10);

  panel.focus(); // can't set focus until visible, will close on focusout
};

const isDirectionUp = (button: HTMLElement) => {
  const specifiedMaxHeight = Number(button.dataset.maxHeight);
  const maxHeight = specifiedMaxHeight > 0 ? specifiedMaxHeight : MAX_HEIGHT;

  const btnRect = button?.getBoundingClientRect();
  if (btnRect) {
    const spaceAbove = btnRect.top;
    const spaceBelow = window.innerHeight - btnRect.bottom;
    return spaceAbove > spaceBelow && spaceBelow < maxHeight + 16;
  }

  return false;
};

const onItemSelected = (dropdownToggleButton: HTMLElement, input: HTMLInputElement) => {
  // We expect input to be wrapped with label
  const parentLabel = input.parentElement as HTMLLabelElement;

  if (!parentLabel) {
    return;
  }

  const buttonTextElement = dropdownToggleButton.querySelector(
    "[data-js-dropdown-text]"
  ) as HTMLElement;

  if (buttonTextElement) {
    buttonTextElement.innerText = parentLabel.innerText;
  }
};

const replaceClasses = (element: HTMLElement, from: string, to: string) => {
  element.classList.remove(...from.split(" ").filter(str => !!str));
  element.classList.add(...to.split(" ").filter(str => !!str));
};

const toggleAllClasses = (dropdownToggleButton: Element, open: boolean) => {
  const outerWrapper = dropdownToggleButton.closest("[data-js-dropdown-outer]");

  const toggleElements = [
    outerWrapper,
    ...(outerWrapper?.querySelectorAll("[data-open-classes], [data-closed-classes]") ??
      []),
  ];

  toggleElements.forEach(element => {
    const htmlElement = element as HTMLElement;
    const openClasses = htmlElement.dataset.openClasses ?? "";
    const closedClasses = htmlElement.dataset.closedClasses ?? "";

    if (open) {
      replaceClasses(htmlElement, closedClasses, openClasses);
    } else {
      replaceClasses(htmlElement, openClasses, closedClasses);
    }
  });
};

export const initializeDropdown = (dropdownToggleButton: Element) => {
  const panelId = dropdownToggleButton.getAttribute("aria-controls");

  if (!panelId) {
    console.warn("Dropdown toggle button is missing an aria-controls attribute");
    return;
  }

  const dropdownPanel = findPanel(dropdownToggleButton);

  if (!dropdownPanel) {
    console.warn(
      `Dropdown toggle button expects a panel with ID ${panelId} but it does not exist`
    );
    return;
  }

  dropdownToggleButton.addEventListener("click", handleButtonClick);
  // prevent taking focus on mousedown (which interferes with focusout)
  dropdownToggleButton.addEventListener("mousedown", e => e.preventDefault());

  dropdownPanel.tabIndex = -1;
  dropdownPanel.addEventListener("focusout", e => handleFocusOut(e as FocusEvent));
  dropdownPanel.addEventListener("keydown", e => handleKeyDown(e as KeyboardEvent));

  // If the dropdown contains form element items (i.e. radio buttons), update the placeholder text
  dropdownPanel.querySelectorAll("input[type='radio']").forEach(input => {
    input.addEventListener("change", () =>
      onItemSelected(dropdownToggleButton as HTMLElement, input as HTMLInputElement)
    );
  });
};
